import { FileDownloadOutlined } from "@mui/icons-material";
import type { TerminalServiceDocument } from "@services/terminal-services/types";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

interface DocumentCardProps {
  document: TerminalServiceDocument;
  testIdBase: string;
  title?: string;
  showDocumentMetadata?: boolean;
  onPreview: () => void;
  onDownload: () => void;
}

interface MetadataItem {
  testId: string;
  label: string;
  value: string;
}

const LANG_BASE = "terminal-services.details.fields";

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  CERTIFICATE: "Certificate",
  DOCUMENT: "Document",
  INSURANCE: "Insurance",
  OTHERS: "Others-Specify",
  PERMIT: "Permit",
  VESSEL_MASTER_SOF: "Vessel Master-SoF",
};

const toTitleCaseLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatDocumentDate = (value?: string | null) => {
  const normalizedValue = value?.trim();

  if (!normalizedValue) return undefined;

  const parsedDate = parseISO(normalizedValue);

  return isValid(parsedDate) ? format(parsedDate, "MM/dd/yyyy") : undefined;
};

const getDocumentTypeLabel = (document: TerminalServiceDocument) => {
  const documentType = document.documentType?.trim();

  if (!documentType) return undefined;

  if (documentType === "OTHERS") {
    return document.otherDocumentType?.trim() || undefined;
  }

  return DOCUMENT_TYPE_LABELS[documentType] ?? toTitleCaseLabel(documentType);
};

const DocumentCard = ({
  document,
  testIdBase,
  title,
  showDocumentMetadata = false,
  onPreview,
  onDownload,
}: DocumentCardProps) => {
  const { t } = useTranslation();
  const fileName = document.documentName;

  const documentTypeLabel = getDocumentTypeLabel(document);
  const issueDate = formatDocumentDate(document.issueDate);
  const expirationDate = formatDocumentDate(document.expirationDate);
  const documentSummary = document.documentSummary;

  const metadataItems: MetadataItem[] = [];

  if (documentTypeLabel) {
    metadataItems.push({
      testId: `${testIdBase}-document-type`,
      label: t(`${LANG_BASE}.document-type`),
      value: documentTypeLabel,
    });
  }

  if (issueDate) {
    metadataItems.push({
      testId: `${testIdBase}-document-issue-date`,
      label: t(`${LANG_BASE}.issue-date`),
      value: issueDate,
    });
  }

  if (expirationDate) {
    metadataItems.push({
      testId: `${testIdBase}-document-expiry-date`,
      label: t(`${LANG_BASE}.expiry-date`),
      value: expirationDate,
    });
  }

  if (documentSummary) {
    metadataItems.push({
      testId: `${testIdBase}-document-summary`,
      label: t(`${LANG_BASE}.document-summary`),
      value: documentSummary,
    });
  }

  return (
    <div
      className="w-full min-w-0 rounded border border-[#E2E8F0] p-4"
      data-testid={`${testIdBase}-document`}
    >
      {title && <p className="mb-6 font-bold text-[#31343B]">{title}</p>}
      <div className="flex justify-start items-center gap-4 w-full">
        <a
          className="inline-flex w-fit max-w-full font-medium text-blue-700 underline break-all cursor-pointer"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onPreview();
          }}
          data-testid={`${testIdBase}-document-link`}
        >
          {fileName}
        </a>

        <button
          className="ml-auto shrink-0 cursor-pointer text-[#31343B]"
          type="button"
          onClick={onDownload}
          aria-label={fileName}
          title="Download"
          data-testid={`${testIdBase}-document-download`}
        >
          <FileDownloadOutlined />
        </button>
      </div>

      {showDocumentMetadata && metadataItems.length > 0 && (
        <div className="mt-6 text-sm leading-5 text-[#31343B]">
          {metadataItems.map(({ testId, label, value }) => (
            <div key={testId} data-testid={testId} className="flex gap-1">
              <span className="shrink-0">{label}:</span>
              <span className="min-w-0 break-words">{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentCard;
